import React, { Component } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import { connect } from 'react-redux';
import { Checkbox, Group, Loader, Stack } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@material-ui/core';
import CustomModal from '.';
import ColorButton from '../Buttons/ColorButton';
import GreenTick from '../../../../../assets/images/module/general/GreenTick.svg';
import RedCross from '../../../../../assets/images/module/general/RedCross.svg';
import SwitchContainer from '../SwitchContainer';
import Autoload from '../../../../Autoload';
import { AI_AGENT_BASE_URL } from '../../../../../helpers/aiAgentApi';

// Bulk counterpart to the AI button inside AssignTransactionModal: analyses every ticked
// transaction in one request and shows the suggestions as a review list the user ticks
// through before anything is written.
//
// The two entry points share the same backend call (POST /suggestTransactionAssignments,
// which takes an array), so this component is purely review + apply.
//
// Applying goes through hellohousing's own bulk endpoints — receiptTenantTransactions/batch/
// tenant and /batch/receipt — the same ones AssignMultipleTransactionsModal uses. Accepted
// rows are grouped by their target (one group per tenant, one per distinct receipt set), so
// twenty transactions across three tenants is three requests rather than twenty.
//
// The reason that matters is not request count. **batch/tenant takes no rentalMonth**: the
// backend derives which month a payment covers, with isEarlyPaymentEnabled selecting the
// convention ("payments in the last 5 days of a month count for the following month").
// hellohousing already owns that logic and gets it right deterministically, so the AI is left
// to decide the genuinely fuzzy part — *which tenant* — and does not guess the month here at
// all. The single-transaction flow still posts an explicit month, because its own dropdown
// does; there the AI's month is a prefill the user sees and can change.
//
// Nothing is applied that the user did not tick. High-confidence rows start ticked because
// that is the case the feature exists for; medium and low start unticked, so accepting a
// weaker guess is always a deliberate act.
//
// A NEW_RECEIPT row creates the receipt and then assigns it, as two requests. A receipt only
// requires name + amount + date + propertyId, so there is nothing the review row cannot
// supply. The payload follows autoAddReceipts in AssignTransactionModal — the existing
// rule-driven "create a receipt from this transaction" path — rather than the manual form's
// defaults, so a receipt created here is indistinguishable from one the rules would have
// created: signed amount, filed against the current billing period, and billing-relevant when
// a cost type is known. VAT and tax classification are the one thing that path takes from a
// saved rule and this one has no basis for, so they stay unset; "Einzeln bearbeiten" opens
// the full receipt form for anyone who wants to set them up front.

const MOBILE_SIZE = 992;

// Matches MAX_TRANSACTIONS_PER_REQUEST in the agent server's transactionAssignmentService.
// Kept as its own constant rather than read from an error response so the modal can say up
// front how many of the selection it will look at, instead of failing after the click.
const MAX_TRANSACTIONS = 50;

// Groups are posted a few at a time. Sequential makes a large batch a long wait; unbounded
// fires every write at hellohousing's API at once, and these are writes, not reads.
const APPLY_CONCURRENCY = 4;

const CONFIDENCE_LABELS = { high: 'hohe Sicherheit', medium: 'mittlere Sicherheit', low: 'geringe Sicherheit' };
const CONFIDENCE_COLORS = { high: '#059266', medium: '#C58C1E', low: '#727293' };

const cellStyle = {
  fontFamily: 'Inter, sans-serif',
  fontSize: '13px',
  fontWeight: '400',
  color: '#111147',
  verticalAlign: 'top',
  padding: '10px 8px',
};

// Whether this row can be carried out from the table. NEW_RECEIPT counts: it just takes one
// extra request (create the receipt, then assign it) rather than extra input from the user.
function isApplicable(suggestion) {
  if (!suggestion) return false;
  if (suggestion.suggestionType === 'RENTAL_INCOME') return Boolean(suggestion.tenantId);
  if (suggestion.suggestionType === 'CREDIT_DEBIT_BALANCE') return (suggestion.receiptIds || []).length > 0;
  if (suggestion.suggestionType === 'NEW_RECEIPT') return Boolean(suggestion.newReceipt?.name);
  return false;
}

function suggestionLabel(suggestion) {
  switch (suggestion?.suggestionType) {
    case 'RENTAL_INCOME':
      // Deliberately no month: batch/tenant derives it, so printing the AI's guess here would
      // promise something the write does not necessarily do.
      return `Mietzahlung: ${suggestion.tenantName || 'Mieter'}`;
    case 'CREDIT_DEBIT_BALANCE':
      return `Beleg: ${(suggestion.receipts || []).map((receipt) => receipt.name).filter(Boolean).join(', ')}`;
    case 'NEW_RECEIPT':
      return `Neuer Beleg: ${suggestion.newReceipt?.name || ''}${suggestion.newReceipt?.costTypeName ? ` (${suggestion.newReceipt.costTypeName})` : ''}`;
    default:
      return 'Kein Vorschlag';
  }
}

export class AiTransactionAssignmentsModal extends Component {
  state = {
    isMobile: window.innerWidth <= MOBILE_SIZE,
    step: 'loading', // 'loading' | 'review' | 'applying' | 'done'
    suggestions: [],
    accepted: {}, // transactionId -> bool
    rowStatus: {}, // transactionId -> 'ok' | 'error'
    error: null,
    appliedCount: 0,
    failedCount: 0,
    currentBillingPeriod: null,
    // Mirrors the switch AssignMultipleTransactionsModal offers for the same batch call.
    isEarlyPayment: false,
  };

  controller = null;

  componentDidMount() {
    if (this.props.open) this.requestSuggestions();
  }

  componentDidUpdate(prevProps) {
    // Each open starts fresh. Carrying suggestions over would show the previous selection's
    // analysis next to a different set of ticked transactions.
    if (!prevProps.open && this.props.open) this.requestSuggestions();
  }

  componentWillUnmount() {
    this.abort();
  }

  abort = () => {
    if (this.controller) {
      this.controller.abort();
      this.controller = null;
    }
  };

  // The transactions this run will actually look at: already-assigned ones are dropped here
  // rather than sent, because the server would only answer "bereits zugeordnet" for them and
  // they would consume slots in the capped batch that unassigned transactions need.
  targets = () => (this.props.transactions || [])
    .filter((transaction) => !transaction?.transactionAssigments)
    .slice(0, MAX_TRANSACTIONS);

  // The billing period a newly created receipt is filed against, matching what
  // autoAddReceipts uses in AssignTransactionModal. Fetched when the modal opens so it is
  // ready by the time the user applies; a receipt is still created without it.
  fetchCurrentBillingPeriod = async () => {
    const { propertySelected } = this.props;
    if (!propertySelected?.id) return;
    try {
      const response = await axios.get(
        `${Autoload.Setting.property_api_base_url}currentBillingPeriod?propertyId=${propertySelected.id}&offset=0&fetch=10`,
        { headers: { 'Content-Type': 'application/json', Authorization: Autoload.Utils.getAuthToken() } }
      );
      this.setState({ currentBillingPeriod: response?.data?.result || null });
    } catch (error) {
      this.setState({ currentBillingPeriod: null });
    }
  };

  requestSuggestions = async () => {
    const { propertySelected, accountData, profileData, selectedBankAccount } = this.props;
    const targets = this.targets();

    this.fetchCurrentBillingPeriod();

    if (targets.length === 0) {
      this.setState({ step: 'review', suggestions: [], error: 'Alle ausgewählten Transaktionen sind bereits zugeordnet.' });
      return;
    }

    const bankAccountId = selectedBankAccount?.id || targets[0]?.bankAccountId;
    if (!bankAccountId) {
      this.setState({ step: 'review', error: 'Für diese Auswahl ist keine KI-Analyse möglich.' });
      return;
    }

    this.abort();
    this.controller = new AbortController();
    this.setState({ step: 'loading', error: null, suggestions: [], accepted: {}, rowStatus: {} });

    try {
      const response = await fetch(`${AI_AGENT_BASE_URL}/suggestTransactionAssignments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: this.controller.signal,
        body: JSON.stringify({
          encryptedToken: profileData?.auth?.encryptedToken,
          accountId: accountData?.accountId,
          userId: profileData?.id,
          propertyId: propertySelected?.id,
          bankAccountId,
          transactionIds: targets.map((transaction) => transaction.id),
        }),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        // 402 carries the credit-gate copy from the server; the other failures already come
        // back as user-facing German.
        this.setState({ step: 'review', error: data?.error || 'Die Vorschläge konnten nicht erstellt werden. Bitte versuchen Sie es erneut.' });
        return;
      }

      const suggestions = data?.result?.suggestions || [];
      const accepted = {};
      suggestions.forEach((suggestion) => {
        // Only high confidence is pre-ticked — see the note at the top of this file.
        if (isApplicable(suggestion) && suggestion.confidence === 'high') accepted[suggestion.transactionId] = true;
      });

      this.setState({ step: 'review', suggestions, accepted });
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.setState({ step: 'review', error: 'Die Vorschläge konnten nicht erstellt werden. Bitte versuchen Sie es erneut.' });
    }
  };

  transactionById = (transactionId) => (this.props.transactions || []).find((transaction) => transaction.id === transactionId);

  // Creates the receipt a NEW_RECEIPT suggestion proposes and returns its new id.
  //
  // Mirrors autoAddReceipts in AssignTransactionModal — the existing rule-driven "create a
  // receipt from this transaction" path — rather than the manual form's defaults, so a receipt
  // created here is indistinguishable from one the rules would have created: signed amount
  // (an expense is negative in hellohousing), filed against the current billing period, and
  // billing-relevant when a cost type is known. VAT and tax classification are the one thing
  // that path takes from a saved rule and this one has no basis for, so they stay unset.
  createReceiptForSuggestion = async (suggestion) => {
    const { propertySelected } = this.props;
    const { currentBillingPeriod } = this.state;
    const transaction = this.transactionById(suggestion.transactionId);
    const draft = suggestion.newReceipt || {};
    const receiptDate = `${dayjs(draft.receiptDate || transaction?.bankBookingDate).format('YYYY-MM-DD')}T00:00:00`;

    const payload = {
      id: null,
      name: draft.name,
      receiptDate,
      date: receiptDate,
      costs: draft.costs,
      cost: draft.costs,
      // Must be a real boolean: the API's model binder rejects null outright
      // ("The JSON value could not be converted to System.Boolean"), and a suggestion
      // without a cost type is common, so autoAddReceipts' `? true : null` is not safe to
      // copy here.
      utilityBillingRelevant: Boolean(draft.costTypeId),
      // A receipt outside any billing period falls back to its own date rather than being
      // created with empty bounds.
      receiptPeriodStart: currentBillingPeriod?.startDate || receiptDate,
      receiptPeriodEnd: currentBillingPeriod?.endDate || receiptDate,
      propertyId: propertySelected?.id,
      selectedCostTypes: draft.costTypeId ? [{ id: draft.costTypeId }] : [],
      vatPercent: null,
      vatAmount: null,
      netAmount: null,
      laborCost: null,
      taxCostType: null,
      receiptNumber: null,
      notes: transaction?.purpose || null,
      heatingCostType: null,
    };

    const response = await axios.post(
      Autoload.Setting.rm_api_base_url + 'create',
      JSON.stringify(payload),
      { headers: { 'Content-Type': 'application/json', Authorization: Autoload.Utils.getAuthToken() } }
    );

    // The create endpoint answers with the new receipt's id as a bare string in `result`
    // (see handleaddReceipt in ReceiptDetailModal).
    const receiptId = response?.data?.result;
    if (!receiptId || typeof receiptId !== 'string') throw new Error('Receipt creation returned no id');
    return receiptId;
  };

  // Groups accepted rows into the fewest batch calls that express them: one per tenant, one
  // per distinct set of receipts. `createdByTransaction` carries the receipts that had to be
  // created first, so a NEW_RECEIPT row joins the receipt grouping like any other.
  buildGroups = (toApply, createdByTransaction) => {
    const { propertySelected } = this.props;
    const { isEarlyPayment } = this.state;
    const groups = new Map();

    const add = (key, url, payload, transactionId) => {
      if (!groups.has(key)) groups.set(key, { url, payload: { ...payload, transactionIds: [] }, transactionIds: [] });
      const group = groups.get(key);
      group.payload.transactionIds.push(transactionId);
      group.transactionIds.push(transactionId);
    };

    for (const suggestion of toApply) {
      const transactionId = suggestion.transactionId;

      if (suggestion.suggestionType === 'RENTAL_INCOME') {
        // No rentalMonth: batch/tenant derives it. See the note at the top of this file.
        add(
          `tenant:${suggestion.tenantId}`,
          Autoload.Setting.finance_api_base_url + 'receiptTenantTransactions/batch/tenant',
          { propertyId: propertySelected?.id, tenantId: suggestion.tenantId, isEarlyPaymentEnabled: isEarlyPayment },
          transactionId
        );
        continue;
      }

      const receiptIds = suggestion.suggestionType === 'NEW_RECEIPT'
        ? [createdByTransaction[transactionId]]
        : (suggestion.receiptIds || []);
      if (receiptIds.length === 0 || !receiptIds[0]) continue;

      // Sorted so two rows pointing at the same receipts land in one group regardless of the
      // order the model listed them in.
      const key = `receipt:${[...receiptIds].sort().join(',')}`;
      add(
        key,
        Autoload.Setting.finance_api_base_url + 'receiptTenantTransactions/batch/receipt',
        { propertyId: propertySelected?.id, receiptIds },
        transactionId
      );
    }

    return [...groups.values()];
  };

  applyAccepted = async () => {
    const { suggestions, accepted } = this.state;
    const toApply = suggestions.filter((suggestion) => accepted[suggestion.transactionId] && isApplicable(suggestion));
    if (toApply.length === 0) return;

    this.setState({ step: 'applying', rowStatus: {} });

    const headers = { 'Content-Type': 'application/json', Authorization: Autoload.Utils.getAuthToken() };
    const failedTransactions = new Set();
    const createdByTransaction = {};

    const markRows = (transactionIds, status) => this.setState((prevState) => {
      const rowStatus = { ...prevState.rowStatus };
      transactionIds.forEach((id) => { rowStatus[id] = status; });
      return { rowStatus };
    });

    // Bounded-concurrency runner, used for both phases below.
    const runAll = async (items, handler) => {
      let next = 0;
      const worker = async () => {
        while (next < items.length) {
          const item = items[next++];
          await handler(item);
        }
      };
      await Promise.all(Array.from({ length: Math.min(APPLY_CONCURRENCY, items.length) }, worker));
    };

    // Phase 1 — create the receipts NEW_RECEIPT rows propose. Not batchable: the name and cost
    // type are per transaction, and batch/receipt's create-form would generate its own name and
    // throw the model's away. A row whose receipt fails to create is marked failed and never
    // reaches phase 2, so no assignment points at a receipt that was never created.
    const toCreate = toApply.filter((suggestion) => suggestion.suggestionType === 'NEW_RECEIPT');
    await runAll(toCreate, async (suggestion) => {
      try {
        createdByTransaction[suggestion.transactionId] = await this.createReceiptForSuggestion(suggestion);
      } catch (error) {
        failedTransactions.add(suggestion.transactionId);
        markRows([suggestion.transactionId], 'error');
      }
    });

    // Phase 2 — one batch call per target.
    const groups = this.buildGroups(
      toApply.filter((suggestion) => !failedTransactions.has(suggestion.transactionId)),
      createdByTransaction
    );

    await runAll(groups, async (group) => {
      try {
        const response = await axios.post(group.url, group.payload, { headers });
        // hellohousing answers 200 with an envelope; a false `status` in it is still a failure
        // (handlePostBulkAssignment in AssignMultipleTransactionsModal checks the same field).
        if (response?.data?.status === false) throw new Error('Batch assignment rejected');
        markRows(group.transactionIds, 'ok');
      } catch (error) {
        // A batch is all-or-nothing from here: every transaction in the group is reported
        // failed, since the endpoint gives no per-transaction result to be more precise with.
        group.transactionIds.forEach((id) => failedTransactions.add(id));
        markRows(group.transactionIds, 'error');
      }
    });

    const failed = failedTransactions.size;
    const applied = toApply.length - failed;
    this.setState({ step: 'done', appliedCount: applied, failedCount: failed });

    showNotification({
      message: failed === 0
        ? `${applied} ${applied === 1 ? 'Transaktion' : 'Transaktionen'} zugeordnet.`
        : `${applied} zugeordnet, ${failed} fehlgeschlagen.`,
      icon: <img src={failed === 0 ? GreenTick : RedCross} alt={failed === 0 ? 'green-tick' : 'red-cross'} />,
      id: 'aiBulkAssignment',
    });
  };

  renderTransactionCell = (transaction) => (
    <Stack gap={2}>
      <span style={{ fontWeight: '500' }}>{transaction?.counterpartName || '—'}</span>
      <span style={{ fontSize: '12px', color: '#727293' }}>
        {transaction?.bankBookingDate ? dayjs(transaction.bankBookingDate).format('DD.MM.YYYY') : ''}
        {' · '}
        {Number(transaction?.amount || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
      </span>
      {transaction?.purpose &&
      <span style={{ fontSize: '12px', color: '#727293', maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {transaction.purpose}
      </span>}
    </Stack>
  );

  renderRow = (suggestion) => {
    const { accepted, rowStatus, step } = this.state;
    const transaction = this.transactionById(suggestion.transactionId);
    const applicable = isApplicable(suggestion);
    const status = rowStatus[suggestion.transactionId];

    return (
      <TableRow key={suggestion.transactionId}>
        <TableCell style={{ ...cellStyle, width: '40px' }}>
          {applicable &&
          <Checkbox
            checked={Boolean(accepted[suggestion.transactionId])}
            disabled={step === 'applying' || step === 'done'}
            onChange={() => this.setState((prevState) => ({
              accepted: { ...prevState.accepted, [suggestion.transactionId]: !prevState.accepted[suggestion.transactionId] },
            }))}
          />}
        </TableCell>

        <TableCell style={cellStyle}>{this.renderTransactionCell(transaction)}</TableCell>

        <TableCell style={cellStyle}>
          <Stack gap={2}>
            <span>{suggestionLabel(suggestion)}</span>
            {suggestion.suggestionType === 'NEW_RECEIPT' &&
            <>
              {/* Spelled out because ticking this row creates a record, not just a link
                  between two that already exist. */}
              <span style={{ fontSize: '12px', color: '#727293' }}>
                wird angelegt: {Number(suggestion.newReceipt?.costs || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                {' · '}{dayjs(suggestion.newReceipt?.receiptDate).format('DD.MM.YYYY')}
                {suggestion.newReceipt?.costTypeName ? ` · ${suggestion.newReceipt.costTypeName}` : ' · ohne Kostenart'}
              </span>
              {step === 'review' &&
              <span
                style={{ fontSize: '12px', color: '#5555FF', cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => this.props.onOpenSingle && this.props.onOpenSingle(transaction)}
              >
                Einzeln bearbeiten
              </span>}
            </>}
          </Stack>
        </TableCell>

        <TableCell style={cellStyle}>
          <Stack gap={2}>
            <span style={{ fontSize: '12px', color: '#727293' }}>{suggestion.reason}</span>
            {applicable && CONFIDENCE_LABELS[suggestion.confidence] &&
            <span style={{ fontSize: '12px', color: CONFIDENCE_COLORS[suggestion.confidence] }}>
              {CONFIDENCE_LABELS[suggestion.confidence]}
              {suggestion.source === 'rule' ? ' · aus bestehender Regel' : ''}
            </span>}
          </Stack>
        </TableCell>

        <TableCell style={{ ...cellStyle, width: '90px' }}>
          {status === 'ok' && <img src={GreenTick} alt="green-tick" style={{ width: '16px', height: '16px' }} />}
          {status === 'error' && <img src={RedCross} alt="red-cross" style={{ width: '16px', height: '16px' }} />}
        </TableCell>
      </TableRow>
    );
  };

  render() {
    const { open, toggle, className, transactions } = this.props;
    const { step, suggestions, accepted, error, appliedCount, failedCount, isEarlyPayment } = this.state;

    const acceptedRows = suggestions.filter((suggestion) => accepted[suggestion.transactionId] && isApplicable(suggestion));
    const acceptedCount = acceptedRows.length;
    const hasRentRow = acceptedRows.some((suggestion) => suggestion.suggestionType === 'RENTAL_INCOME');
    const analysed = this.targets().length;
    const totalSelected = (transactions || []).length;

    return (
      <CustomModal
        heading={'KI-Zuordnungsvorschläge'}
        headingClassName={'tooltips ai-transaction-assignments-modal-heading'}
        open={open}
        toggle={() => toggle(step === 'done')}
        className={`${className}`}
        style={{ maxWidth: '1100px' }}
      >
        <Stack gap={16}>
          {step === 'loading' &&
          <Group gap={12} align="center">
            <Loader size={'sm'} />
            <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#111147' }}>
              {analysed} {analysed === 1 ? 'Transaktion wird' : 'Transaktionen werden'} analysiert…
            </span>
          </Group>}

          {error &&
          <Group justify="space-between" wrap="nowrap" align="center">
            <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#E8193C' }}>{error}</span>
            <ColorButton text={'Erneut versuchen'} className="gray-btn" onClick={() => this.requestSuggestions()} />
          </Group>}

          {/* Said up front rather than after the click: the selection is larger than one
              request analyses, and the user needs to know which part they are looking at. */}
          {totalSelected > analysed && step !== 'loading' && !error &&
          <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: '#727293' }}>
            {analysed} von {totalSelected} ausgewählten Transaktionen wurden analysiert (bereits zugeordnete werden übersprungen, maximal {MAX_TRANSACTIONS} pro Durchlauf).
          </span>}

          {suggestions.length > 0 &&
          <TableContainer style={{ maxHeight: '55vh' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell style={{ ...cellStyle, fontWeight: '500' }} />
                  <TableCell style={{ ...cellStyle, fontWeight: '500' }}>Transaktion</TableCell>
                  <TableCell style={{ ...cellStyle, fontWeight: '500' }}>Vorschlag</TableCell>
                  <TableCell style={{ ...cellStyle, fontWeight: '500' }}>Begründung</TableCell>
                  <TableCell style={{ ...cellStyle, fontWeight: '500' }} />
                </TableRow>
              </TableHead>
              <TableBody>{suggestions.map((suggestion) => this.renderRow(suggestion))}</TableBody>
            </Table>
          </TableContainer>}

          {step === 'done' &&
          <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: failedCount > 0 ? '#E8193C' : '#059266' }}>
            {failedCount > 0
              ? `${appliedCount} zugeordnet, ${failedCount} fehlgeschlagen. Die fehlgeschlagenen Transaktionen können einzeln zugeordnet werden.`
              : `${appliedCount} ${appliedCount === 1 ? 'Transaktion wurde' : 'Transaktionen wurden'} zugeordnet.`}
          </span>}

          {/* Only relevant when a rent assignment is actually going to be made — this is the
              flag batch/tenant uses to decide which month a payment covers. */}
          {step === 'review' && hasRentRow &&
          <SwitchContainer
            switchText={<span>Mietzahlungen, die in den letzten 5 Tagen eines Monats eingehen, für den Folgemonat berücksichtigen</span>}
            checked={isEarlyPayment}
            onChange={() => this.setState({ isEarlyPayment: !isEarlyPayment })}
            textClassName={'multiple-transaction-is-early-rental-payment-switch-label'}
          />}

          <Group justify="space-between" align="center">
            <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#727293' }}>
              {step === 'review' && suggestions.length > 0 && `${acceptedCount} von ${suggestions.length} werden zugeordnet`}
            </span>
            <Group gap={12}>
              <ColorButton
                text={step === 'done' ? 'Schließen' : 'Abbrechen'}
                className="gray-btn"
                onClick={() => toggle(step === 'done')}
              />
              {step !== 'done' &&
              <ColorButton
                text={'Zuordnen'}
                className="purple-btn"
                showLoader={step === 'applying'}
                disabled={step !== 'review' || acceptedCount === 0}
                onClick={() => this.applyAccepted()}
              />}
            </Group>
          </Group>
        </Stack>
      </CustomModal>
    );
  }
}

const mapStateToProps = (state) => ({
  propertySelected: state.generalReducer.property,
  accountData: state.generalReducer.accountData,
  profileData: state.generalReducer.profile,
});

export default connect(mapStateToProps)(AiTransactionAssignmentsModal);
